import React from "react";
import Modal from "react-modal";
import moment from "moment-timezone";
import { google, yahoo, outlook } from "calendar-link";

interface TimeBlock {
  start: moment.Moment;
  end: moment.Moment;
}

interface CalendarEvent {
  title: string;
  description?: string;
  start: Date;
  end: Date;
  location?: string;
}

interface AddToCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventDetails: {
    title: string;
    description?: string;
    location?: string;
  };
  isOldSchedule: boolean;
  schedule: any;
}

const AddToCalendarModal: React.FC<AddToCalendarModalProps> = ({
  isOpen,
  onClose,
  eventDetails,
  isOldSchedule,
  schedule,
}) => {
  const getEventTimes = (): TimeBlock[] => {
    if (isOldSchedule) {
      return getOldScheduleEventTimes();
    }

    return getNewScheduleEventTimes();
  };

  const getOldScheduleEventTimes = (): TimeBlock[] => {
    if (schedule.schedule?.length) {
      const userTimeZone = moment.tz.guess();
      const userUTCOffset = moment.tz(userTimeZone).utcOffset() / 60;

      const scheduleDates = schedule.schedule.map((date: string) =>
        moment.utc(date).tz(userTimeZone)
      );

      const groupedBlocks: moment.Moment[][] = [];
      let currentGroup: moment.Moment[] = [];

      let previousDate = scheduleDates[0];

      scheduleDates.forEach((dateObj: moment.Moment, index: number) => {
        const isSameDay =
          dateObj.format("YYYY-MM-DD") === previousDate.format("YYYY-MM-DD");

        if (isSameDay && dateObj.hours() === previousDate.hours() + 1) {
          currentGroup.push(dateObj);
        } else {
          if (currentGroup.length) {
            groupedBlocks.push(currentGroup);
          }

          currentGroup = [dateObj];
        }

        previousDate = dateObj;
      });

      if (currentGroup.length) {
        groupedBlocks.push(currentGroup);
      }

      // Create time blocks from grouped blocks
      return groupedBlocks.map((group) => {
        const start = group[0];
        const end = group[group.length - 1].clone().add(1, 'hour'); // Add one hour to the last time slot
        return {
          start,
          end,
        };
      });
    }
    return [];
  };

  const getNewScheduleEventTimes = (): TimeBlock[] => {
    const blocksList = schedule.blocks;
    const planningTimeZone = schedule.timeZone;
    const userTimeZone = moment.tz.guess();

    return blocksList.map((block: any) => ({
      start: moment(
        `${block.fullDate} ${block.startTime}`,
        "YYYY-MM-DD hh:mm A",
        planningTimeZone
      ).tz(userTimeZone),
      end: moment(
        `${block.fullDate} ${block.endTime}`,
        "YYYY-MM-DD hh:mm A",
        planningTimeZone
      ).tz(userTimeZone),
    }));
  };

  const generateGoogleCalendarUrl = (): string[] => {
    const eventTimes = getEventTimes();
    if (!eventTimes.length) return [];
      
    const userTimeZone = moment.tz.guess();

    const firstEvent = eventTimes[0];
    const start = moment(firstEvent.start).tz(userTimeZone);
    const end = moment(firstEvent.end).tz(userTimeZone);
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: eventDetails.title,
      dates: `${start.format('YYYYMMDDTHHmmss')}/${end.format('YYYYMMDDTHHmmss')}`,
      details: eventDetails.description || '',
      location: eventDetails.location || '',
      ctz: userTimeZone,
    });

    console.log("eventDetails", eventDetails);
    return [`https://calendar.google.com/calendar/render?${params.toString()}`];
  };

  const generateYahooCalendarUrl = (): string[] => {
    const eventTimes = getEventTimes();
    if (!eventTimes.length) return [];
    
    const userTimeZone = moment.tz.guess();


    const firstEvent = eventTimes[0];
    const start = moment(firstEvent.start).tz(userTimeZone);
    const end = moment(firstEvent.end).tz(userTimeZone);
    const events: CalendarEvent[] = [{
      title: eventDetails.title,
      description: eventDetails.description,
      start: start.toDate(),
      end: end.toDate(),
      location: eventDetails.location,
    }];
    
    // Create URLs for all events
    return events.map((event) => yahoo(event));
  };

  const generateOutlookCalendarUrl = (): string[] => {
    const eventTimes = getEventTimes();
    if (!eventTimes.length) return [];
    
    const userTimeZone = moment.tz.guess();
    
    const events: CalendarEvent[] = eventTimes.map((timeBlock) => {
      // Convert UTC times to user's local timezone
      const start = moment(timeBlock.start).tz(userTimeZone);
      const end = moment(timeBlock.end).tz(userTimeZone);
      
      return {
        title: eventDetails.title,
        description: eventDetails.description,
        start: start.toDate(),
        end: end.toDate(),
        location: eventDetails.location,
      };
    });

    // Create URLs for all events
    return events.map((event) => outlook(event));
  };

  const generateICSFile = () => {
    const eventTimes = getEventTimes();
    if (!eventTimes.length) return "";

    const userTimeZone = moment.tz.guess();
    
    // Format date for ICS
    const formatDate = (date: Date) => {
      return moment(date).format('YYYYMMDDTHHmmss');
    };

    // Create ICS content
    let icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//ClassX//Calendar Events//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'X-WR-CALNAME:ClassX Events',
      'X-WR-TIMEZONE:' + userTimeZone,
    ];

    // Add each event
    eventTimes.forEach((timeBlock, index) => {
      const start = moment(timeBlock.start).tz(userTimeZone);
      const end = moment(timeBlock.end).tz(userTimeZone);

      icsContent = icsContent.concat([
        'BEGIN:VEVENT',
        'DTSTART:' + formatDate(start.toDate()),
        'DTEND:' + formatDate(end.toDate()),
        'SUMMARY:' + eventDetails.title,
        eventDetails.description ? 'DESCRIPTION:' + eventDetails.description.replace(/\n/g, '\\n') : '',
        eventDetails.location ? 'LOCATION:' + eventDetails.location : '',
        'UID:classx-event-' + index + '-' + Date.now(),
        'DTSTAMP:' + formatDate(new Date()),
        'END:VEVENT'
      ]);
    });

    icsContent.push('END:VCALENDAR');

    // Create blob and download link
    const blob = new Blob([icsContent.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    return url;
  };

  const handleCalendarClick = (urls: string[]) => {
    if (!urls.length) return;
    
    // Open each URL in a new tab with a small delay
    urls.forEach((url, index) => {
      setTimeout(() => {
        window.open(url, '_blank');
      }, index * 500); // 500ms delay between each open
    });
    
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} style={modalStyles}>
      <div className="w-full">
        {/* Modal Header */}
        <div className="flex justify-between items-center px-5 pt-3.5 pb-2 border-b border-[#F5F5F5]">
          <h3 className="font-inter font-bold text-[16px] leading-[25px] text-[#1D0839]">
            Add to Calendar
          </h3>
          <button
            className="w-[34px] h-[34px] flex justify-center items-center rounded-full p-[8px] border border-[#DFDFDF]"
            onClick={onClose}
          >
            <svg
              width="18"
              height="19"
              viewBox="0 0 18 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.0687 13.432L9.00068 9.5M12.9327 5.56802L9.00068 9.5M9.00068 9.5L5.0687 5.56802M9.00068 9.5L12.9327 13.432"
                stroke="#0D0D0D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex flex-col px-5 py-3.5 space-y-[14px]">
          <p className="font-inter font-normal text-[14px] leading-[24px] text-[#373737] text-center">
            Keep track of every class by syncing your schedule.
          </p>

          <div className="flex flex-col space-y-2">
            {/* Google Calendar button */}
            <button
              type="button"
              className="group w-full rounded-[12px] border border-[#DFDFDF] py-[12px] px-[16px] flex justify-between items-center bg-[#FCFCFF] hover:bg-[#F4F4FA] hover:border-transparent transition-all duration-300"
              onClick={() => handleCalendarClick(generateGoogleCalendarUrl())}
            >
              <div className="flex items-center space-x-[12px]">
                <img
                  src="/images/add-to-calendar/googleCalendar.svg"
                  alt="Google Calendar"
                  width={16}
                  height={16}
                />
                <span className="font-inter font-semibold text-[14px] leading-[26px] text-[#416585]">
                  Google Calendar
                </span>
              </div>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="stroke-[#416585] group-hover:stroke-[#8755E0] transition-all duration-300"
              >
                <path
                  d="M1.5 6H10.875M10.875 6L6.375 1.5M10.875 6L6.375 10.5"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Yahoo Calendar button */}
            <button
              type="button"
              className="group w-full rounded-[12px] border border-[#DFDFDF] py-[12px] px-[16px] flex justify-between items-center bg-[#FCFCFF] hover:bg-[#F4F4FA] hover:border-transparent transition-all duration-300"
              onClick={() => handleCalendarClick(generateYahooCalendarUrl())}
            >
              <div className="flex items-center space-x-[12px]">
                <img
                  src="/images/add-to-calendar/yahoo.svg"
                  alt="Yahoo Calendar"
                  width={16}
                  height={16}
                />
                <span className="font-inter font-semibold text-[14px] leading-[26px] text-[#416585]">
                  Yahoo Calendar
                </span>
              </div>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="stroke-[#416585] group-hover:stroke-[#8755E0] transition-all duration-300"
              >
                <path
                  d="M1.5 6H10.875M10.875 6L6.375 1.5M10.875 6L6.375 10.5"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Outlook Calendar button */}
            <button
              type="button"
              className="group w-full rounded-[12px] border border-[#DFDFDF] py-[12px] px-[16px] flex justify-between items-center bg-[#FCFCFF] hover:bg-[#F4F4FA] hover:border-transparent transition-all duration-300"
              onClick={() => handleCalendarClick(generateOutlookCalendarUrl())}
            >
              <div className="flex items-center space-x-[12px]">
                <img
                  src="/images/add-to-calendar/microsoft.svg"
                  alt="Outlook Calendar"
                  width={16}
                  height={16}
                />
                <span className="font-inter font-semibold text-[14px] leading-[26px] text-[#416585]">
                  Outlook Calendar
                </span>
              </div>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="stroke-[#416585] group-hover:stroke-[#8755E0] transition-all duration-300"
              >
                <path
                  d="M1.5 6H10.875M10.875 6L6.375 1.5M10.875 6L6.375 10.5"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    borderRadius: "24px",
    border: "1px solid var(--Primary-Gray-100, rgba(245, 245, 245, 1))",
    transform: "translate(-50%, -50%)",
    boxShadow: "0px 12px 16px -4px rgba(0, 0, 0, 0.1)",
    backgroundColor: "var(--Primary-Gray-50)",
    padding: "0",
    width: "420px",
    height: "300px",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    zIndex: "30",
  },
};

export default AddToCalendarModal;
